import { DTO__base } from '@shared/common/DTO__base';
import { ENUM__dto_class_name } from '@shared/serialization/ENUM__dto_class_name';
import { DTO__generation } from '../DTO__generation';
import { DTO_TREE__generations_sliding_window } from './DTO_TREE__generations_sliding_window';

export class DTO_TREE__generations extends DTO__base {
  static _class_name_static: string = ENUM__dto_class_name.ABSTRACT;

  /*
   * STATIC
   */
  static create(generations: DTO__generation[]) {
    let out = new DTO_TREE__generations();

    for (let item of generations) {
      out.items_map.set(item._id, item);

      let { previous_generation_id } = item;

      if (previous_generation_id.is_empty_object_id()) {
        out.root_items.push(item);
      } else {
        out.children_map.add_item_to_array(previous_generation_id, item);
      }
    }

    return out;
  }

  /*
   * FIELDS
   */

  //key - generation_id
  items_map: Map<string, DTO__generation>;
  //key - generation_id
  private children_map: Map<string, DTO__generation[]>;

  private root_items: DTO__generation[];

  /*
   * METHODS
   */
  constructor() {
    super();

    this.root_items = [];

    this.children_map = new Map<string, DTO__generation[]>();
    this.items_map = new Map<string, DTO__generation>();
  }

  /**
   * Returns siblings of a node (including itself).
   * If no parentId, returns root nodes.
   */
  get_children_for(generation_id: string): DTO__generation[] {
    if (generation_id.is_empty_object_id()) return [...this.root_items];

    return this.children_map.get_or_assert(generation_id);
  }

  /**
   * Generates a dynamic array of columns (sliding window) ending at the selected item's tier.
   * Format: [ ...[Grandparent Siblings], [Parent Siblings], [Selected + Siblings] ]
   */
  get_sliding_window(generation_current_id: string, columns_max: number) {
    let out = new DTO_TREE__generations_sliding_window();

    /*
     * COLUMNS
     */

    // 1. Check for children of the current node first
    // Using map.get instead of get_or_assert to avoid crashes if children_map doesn't have the key yet
    const children = this.children_map.get_or_create_array(generation_current_id);

    if (children.length > 0) {
      out.columns.unshift(children); // This will be our right-most column eventually
    }

    let item_current = this.items_map.get_or_assert(generation_current_id);

    // Walk UP the tree starting from the selected item's tier to build columns backwards
    while (item_current && out.columns.length < columns_max) {
      const parent_id = item_current.previous_generation_id;

      // 1. Get siblings for the current tier (uses root_items if parent_id is empty)
      const is_root = parent_id.is_empty_object_id();
      const siblings = this.get_children_for(parent_id);

      out.columns.unshift(siblings); // Add to the beginning of our column array

      // 2. If we just processed the root level, we cannot climb any higher. Break here.
      if (is_root) break;

      // 3. Move up to parent for the next iteration
      item_current = this.items_map.get_or_assert(parent_id);
    }

    /*
     * GENERATIONS IDS PATH
     */

    item_current = this.items_map.get_or_assert(generation_current_id);

    let root_reached = false;

    while (!root_reached) {
      out.generations_ids_path.push(item_current._id);

      if (item_current.previous_generation_id.is_empty_object_id()) {
        root_reached = true;
      } else {
        item_current = this.items_map.get_or_assert(item_current.previous_generation_id);
      }
    }

    return out;
  }
}
